import React from "react";

import colors from "core/colors";

import Icon from "core/ReusableIcons/Icon";
import CircleInfinity from "core/ReusableIcons/CircleInfinity";
import Grow from "core/ReusableIcons/Grow";
import Adobe from "core/PartnerIcons/Adobe";
import Squarespace from "core/PartnerIcons/Squarespace";
import Typeform from "core/PartnerIcons/Typeform";
import Mural from "core/PartnerIcons/Mural";
import Zazzle from "core/PartnerIcons/Zazzle";

import caseOneImg from "./images/case_1.png";
import caseTwoImg from "./images/case_2.png";
import caseThreeImg from "./images/case_3.png";

import Api from "core/ReusableIcons/Api";

// Meta / SEO

export const metaTitle = "Developers | Noun Project";
export const metaDescription =
    "Creating, Sharing and Celebrating the World's Visual Language.";

const contactUs =
    "mailto:info@thenounproject.com?subject=Wanted to get in contact";

// Section 1: Build with Noun API
export const mainHeading = "Build with Noun Project's Icon API";

export const mainSubHeading = [
    "Bring the world's most comprehensive, high-quality collection of millions of SVG and PNG icons into your application. Free starter plans, unlimited scalability."
];

export const getStartedBtn = [
    {
        text: "Create & Manage Apps Now",
        href: "/developers/apps/"
    }
];

// Section 2: Bullets
export const bulletAPIFeatures = [
    {
        icon: <Icon fillColor={colors.secondary.orca} size="xl" />,
        title: "Millions of Icons",
        text: "professionally curated and royalty-free"
    },
    {
        icon: <CircleInfinity fillColor={colors.secondary.orca} size="xl" />,
        title: "Infinitely Reliable",
        text: "99.9% uptime, built on AWS"
    },
    {
        icon: <Grow fillColor={colors.secondary.orca} size="xl" />,
        title: "Scale as you Grow",
        text: "paid plans start at $150"
    }
];

// Section 3: Social Proof
export const socialProofHeading = "Trusted by Brands You Know";

export const socialProofIcons = [
    {
        icon: <Adobe title="Adobe" />,
        title: "Adobe",
        link: "https://www.adobe.com/",
        linkTarget: "_blank"
    },
    {
        icon: <Squarespace title="Squarespace" />,
        title: "Squarespace"
    },
    {
        icon: <Typeform title="Typeform" />,
        title: "Typeform"
    },
    {
        icon: <Mural title="Mural" />,
        title: "Mural"
    },
    {
        icon: <Zazzle title="Zazzle" />,
        title: "Zazzle"
    }
];

// section 4: Easy to get started
export const sectionFourHeading = "Easy to Get Started";

export const sectionFourSubHeading = [
    "Noun Project’s REST API, secured with OAuth, easily integrates & scales with any website or app so you’re up and running in minutes."
];

export const sectionFourBtns = [
    {
        text: "Explore API",
        href: "https://api.thenounproject.com/explorer",
        target: "_blank"
    },
    {
        text: "See Documentation",
        href: "https://api.thenounproject.com/",
        target: "_blank"
    }
];

// Section 5: Testimonial
export const testimonials = [
    {
        text:
            '"Building a Noun Project integration made it easier for our customers to turn their ideas into beautiful visual stories."',
        source: "Trevor O'Brien, CEO of Projector"
    }
];

// Section 6: Case Studies
export const caseStudiesTitle = "Case Studies";
export const caseStudiesSubTitle =
    "From design and collaboration tools to digital experiences and physical products, check out how some of our customers are using Noun Project's API.";

export const caseStudies = [
    {
        title: "Mural",
        category: "Use Case - Collaboration",
        imgSrc: caseOneImg,
        url:
            "https://blog.thenounproject.com/remote-work-and-collaboration-get-visual-with-mural-and-noun-project/",
        target: "_blank",
        imageIsLink: true,
        headingIsLink: true,
        subtitleIsLink: true
    },
    {
        title: "Typeform",
        category: "Use Case - Surveys",
        imgSrc: caseTwoImg,
        url: "https://blog.thenounproject.com/noun-project-x-typeform/",
        target: "_blank",
        imageIsLink: true,
        headingIsLink: true,
        subtitleIsLink: true
    },
    {
        title: "Projector",
        category: "Use Case - Design Tool",
        imgSrc: caseThreeImg,
        url: "https://blog.thenounproject.com/projector-noun-project/",
        target: "_blank",
        imageIsLink: true,
        headingIsLink: true,
        subtitleIsLink: true
    }
];

// Section 7: Cards
export const planTitle = "Icon API Plans";
export const cards = [
    {
        icons: [<Api key="Api" size="xl" />],
        title: "Free Starter Plan",
        description: "Up to 5,000 calls per month",
        priceSubHeading: "",
        price: "0",
        priceDuration: "/month",
        buttons: [
            {
                text: "Get Started",
                href: "/developers/apps/"
            }
        ]
    },
    {
        icons: [
            <Api key="Api 1" size="xl" />,
            <Api key="Api 2" size="xl" />,
            <Api key="Api 3" size="xl" />
        ],
        title: "Icon API Pro",
        description: "5,000+ calls per month",
        priceSubHeading: "Starting at",
        price: "150",
        priceDuration: "/month",
        buttons: [
            {
                text: "Contact Us",
                href: contactUs
            }
        ]
    }
];

// Section 8: FAQ
export const faqTitle = "Common Questions";

const faqText = (
    <>
        Build your app with our free API plan. As you grow, pricing starts at
        $150/month depending on call volume. Please {""}
        <a href={contactUs}>contact us</a>
        {""} to customize a plan.
    </>
);

export const faq = [
    {
        title: "How much does API access cost?",
        text: faqText
    },
    {
        title: "How can I track my API usage?",
        text:
            "API accounts include a customer dashboard that lets you monitor API call volume and application usage in real time. The dashboard provides hourly, daily, and monthly information."
    },
    {
        title: "How can I get SVG icon files?",
        text:
            "SVGs are available as part of our paid API plans. Free API keys have access to PNG files and public domain icon SVGs."
    },
    {
        title: "What are acceptable uses for the API? ",
        text:
            "The API can be used to customize physical products, in design and collaboration tools, and more. You may not use the API to replicate Noun Project or to distribute or resell unmodified icons."
    },
    {
        title: "How many products can I connect to one API key?",
        text:
            "There is no limit to the number of products that can be connected to a single API key."
    },
    {
        title: "What icon sizes are offered in the API?",
        text:
            "Icon PNGs are 200 x 200 pixels. Paid plans include access to SVG files which can be easily scaled to any size."
    }
];
